export const DemographicTypeKeys = [
    "affordability",
    "employment",
    "households",
    "housing_completions",
    "housing_starts",
    "median_income",
    "population_15_24",
    "population_25_34",
    "population_35_44",
    "population_45_54",
    "population_55_64",
    "population_over_65",
    "total_population",
] as const;

export type TDemographicType = (typeof DemographicTypeKeys)[number];

/** One period's value in a series. `value` is null where the source cell was blank. */
export interface IDemographicSeriesValue {
    date: Date;
    value: number | null;
}

/**
 * One demographic series: a single series of one type for a single county, with its full period
 * range.
 *
 * A type can carry several series per county. The population age-band types each hold two five-year
 * Census sub-bands (15-24 is 15-19 + 20-24, and so on); `population_over_65` is the exception,
 * holding a single 65+ series with no upper split. **Consumers sum every series for a
 * `(type, fips)` pair** — correct for both shapes, and no per-type special-casing needed.
 *
 * Most types identify each row with a source mnemonic, which names the band it holds; the rest
 * identify rows only by FIPS, and there `series` falls back to the row's position in the file.
 *
 * Units differ per type and values are stored exactly as the source exports them, unscaled:
 * `affordability` is an index (~100-250), `median_income` is dollars, and the population/household
 * types are in thousands. Verified against affordability, median_income, total_population and
 * population_15_24; the rest are assumed to follow their family and are worth confirming before
 * display.
 *
 * Periods are annual, stamped to Dec-1 UTC, and extend past the present — every type forecasts out
 * to Dec-2056, while start years range from 1970 to 2020. Pick a period deliberately rather than
 * taking the last value.
 */
export interface IDemographicSeries {
    type: TDemographicType;
    fips: string;
    geography: string;
    /**
     * Series identity within the type — the source mnemonic where the export provides one
     * (`FPOP1519A.AK013`, whose band is encoded in it), otherwise the row's position among the rows
     * sharing its FIPS. Unique with `type` and `fips`, so a series exported twice collapses to one
     * document rather than double-counting in a sum.
     */
    series: string;
    values: IDemographicSeriesValue[];
    /**
     * When the ETL last wrote this document. Per-document rather than per-job because the job
     * continues past a failing type, so this is what distinguishes a series refreshed by the latest
     * run from one left behind by it.
     */
    runAt: Date;
}
